/**
 * Inky artwork: the single source of truth for the mascot's SVG.
 *
 * Kept free of React and JSX on purpose. `InkyMascot` turns the node tree
 * returned by {@link buildInkyTree} into React elements, and
 * {@link renderInkyMarkup} turns the same tree into an SVG string (used for
 * standalone previews and anywhere a plain string is needed), so both paths
 * draw exactly the same octopus.
 *
 * Geometry lives in a 64x64 user-space viewBox. CSS transforms on SVG
 * children default to `transform-box: view-box`, so a `transform-origin` such
 * as `19px 40px` refers to user-space coordinates and stays correct at every
 * rendered size.
 */

/** Class prefix for every element and keyframe. Unique to avoid collisions. */
export const INKY_CLASS = 'q-inky';

/** Supported moods. Anything else falls back to `idle`. */
export const INKY_MOODS = ['idle', 'thinking', 'writing', 'happy', 'error'];

/**
 * Mascot palettes, tuned per colour scheme. The dark palette lifts the body
 * so the silhouette keeps at least 3:1 against the dark surfaces
 * (#141312 / #1C1B19); the light palette sits in the brand accent family
 * (#4F3BD6) and keeps 3:1+ against #FFFFFF / #F6F5F2.
 */
export const INKY_PALETTES = {
  light: {
    body: '#5A46DE',
    bodyLight: '#7A68EC',
    shade: '#4632C8',
    spot: '#9C8FF3',
    belly: '#C4BBF9',
    sucker: '#C9C0FA',
    highlight: '#FFFFFF',
    eyeWhite: '#FFFFFF',
    pupil: '#17142B',
    ink: '#1E1450',
    cheek: '#FF8FB0',
    sweat: '#8EC8F2',
    bubbleFill: '#FFFFFF',
    bubbleStroke: '#D3CFC7',
    bubbleDot: '#5E5A54',
    feather: '#FFFFFF',
    featherVane: '#D9D4CB',
    featherStroke: '#B9B3A9',
    nib: '#3D3A35',
    inkLine: '#3A28B8',
  },
  dark: {
    body: '#8B7BFF',
    bodyLight: '#A99DFF',
    shade: '#7060EE',
    spot: '#B9AFFF',
    belly: '#D7D1FF',
    sucker: '#D2CBFF',
    highlight: '#FFFFFF',
    eyeWhite: '#FFFFFF',
    pupil: '#141225',
    ink: '#1E1450',
    cheek: '#FF9DBB',
    sweat: '#9AD0F5',
    bubbleFill: '#2A2825',
    bubbleStroke: '#57534D',
    bubbleDot: '#D6D3CD',
    feather: '#F4F2EE',
    featherVane: '#C9C5BE',
    featherStroke: '#8F8A83',
    nib: '#EDEBE7',
    inkLine: '#B9AFFF',
  },
};

/**
 * Tentacle definitions: root point (rotation origin) and path. Left three are
 * drawn by hand; the right three mirror them around x = 32 so Inky stays
 * symmetrical.
 * @type {{ root: [number, number], d: string }[]}
 */
const LEFT_TENTACLES = [
  { root: [19.4, 39], d: 'M19.4 39 C 13.6 40.8, 8.8 44.2, 8.6 49 C 8.4 53.2, 12.8 54.8, 14.4 52' },
  { root: [24.4, 41.6], d: 'M24.4 41.6 C 21.6 45.8, 17.8 50.2, 18.2 54.4 C 18.6 58.2, 23.2 59, 24 56' },
  { root: [29.6, 42.6], d: 'M29.6 42.6 C 28.8 47.2, 27 51.4, 27 55.4 C 27 58.6, 29.6 59.6, 30 57.2' },
];

/**
 * Mirrors an SVG path made only of absolute M/C commands around x = 32.
 * @param {string} d Path data.
 * @returns {string} Mirrored path data.
 */
function mirrorPath(d) {
  return d.replace(/(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g, (match, x, y) => {
    const mirrored = Math.round((64 - Number(x)) * 100) / 100;
    return `${mirrored} ${y}`;
  });
}

const TENTACLES = [
  ...LEFT_TENTACLES,
  ...LEFT_TENTACLES.slice()
    .reverse()
    .map(({ root, d }) => ({ root: [64 - root[0], root[1]], d: mirrorPath(d) })),
];

/**
 * When writing, the outer right tentacle reaches up to hold the quill.
 */
const WRITING_ARM = { root: [45, 39], d: 'M45 39 C 50.6 40.4, 54 43.4, 53.4 48.6' };

/**
 * Small node helper: `[tag, attrs, children]`.
 * @param {string} tag SVG tag name.
 * @param {Object<string, string|number>} [attrs] SVG attributes (SVG spelling, e.g. `stroke-width`).
 * @param {Array} [children] Child nodes.
 * @returns {{ tag: string, attrs: Object, children: Array }} Node.
 */
function h(tag, attrs = {}, children = []) {
  return { tag, attrs, children };
}

/**
 * Normalises a mood name.
 * @param {string} mood Requested mood.
 * @returns {string} A supported mood.
 */
export function normalizeMood(mood) {
  return INKY_MOODS.includes(mood) ? mood : 'idle';
}

/**
 * Turns an arbitrary id (React's `useId()` returns `:r1:`) into a value that
 * is safe inside `url(#...)` references.
 * @param {string} raw Raw id.
 * @returns {string} Safe id.
 */
export function sanitizeId(raw) {
  return String(raw || 'inky').replace(/[^a-zA-Z0-9_-]/g, '') || 'inky';
}

/**
 * Builds the face: eyes, brows, cheeks and mouth for a mood.
 * @param {string} mood Normalised mood.
 * @param {Object} p Palette.
 * @returns {Array} Nodes.
 */
function buildFace(mood, p) {
  const nodes = [];
  const c = INKY_CLASS;

  if (mood === 'happy') {
    // Closed, smiling eyes: ^ ^
    nodes.push(
      h('g', { class: `${c}__eyes ${c}__eyes--closed`, fill: 'none', stroke: p.pupil, 'stroke-width': 2.4, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
        h('path', { d: 'M21.2 29.4 Q 25 24.6 28.8 29.4' }),
        h('path', { d: 'M35.2 29.4 Q 39 24.6 42.8 29.4' }),
      ]),
    );
  } else {
    // Pupil offsets give each mood a gaze direction.
    const gaze = {
      idle: [0.4, 0.5],
      thinking: [0.9, -1.1],
      writing: [1.3, 1.2],
      error: [0, 1],
    }[mood] || [0, 0];
    const [gx, gy] = gaze;
    nodes.push(
      h('g', { class: `${c}__eyes` }, [
        h('ellipse', { cx: 25, cy: 28, rx: 5.1, ry: 5.7, fill: p.eyeWhite }),
        h('ellipse', { cx: 39, cy: 28, rx: 5.1, ry: 5.7, fill: p.eyeWhite }),
        h('g', { class: `${c}__pupils` }, [
          h('circle', { cx: 25 + gx, cy: 28.6 + gy, r: 3.4, fill: p.pupil }),
          h('circle', { cx: 39 + gx, cy: 28.6 + gy, r: 3.4, fill: p.pupil }),
          h('circle', { cx: 26.4 + gx, cy: 27 + gy, r: 1.25, fill: p.highlight }),
          h('circle', { cx: 40.4 + gx, cy: 27 + gy, r: 1.25, fill: p.highlight }),
          h('circle', { cx: 24 + gx, cy: 30.2 + gy, r: 0.6, fill: p.highlight }),
          h('circle', { cx: 38 + gx, cy: 30.2 + gy, r: 0.6, fill: p.highlight }),
        ]),
      ]),
    );
  }

  if (mood === 'error') {
    // Worried brows: inner ends raised.
    nodes.push(
      h('g', { class: `${c}__brows`, fill: 'none', stroke: p.ink, 'stroke-width': 1.7, 'stroke-linecap': 'round' }, [
        h('path', { d: 'M20.6 21.6 Q 24 21.2 27.6 19.2' }),
        h('path', { d: 'M43.4 21.6 Q 40 21.2 36.4 19.2' }),
      ]),
    );
  }

  const cheekOpacity = mood === 'happy' ? 0.75 : 0.55;
  nodes.push(
    h('g', { class: `${c}__cheeks`, fill: p.cheek, opacity: cheekOpacity }, [
      h('ellipse', { cx: 19.4, cy: 34.4, rx: 2.9, ry: 1.8 }),
      h('ellipse', { cx: 44.6, cy: 34.4, rx: 2.9, ry: 1.8 }),
    ]),
  );

  const mouths = {
    idle: h('path', { d: 'M29.2 34.6 Q 32 37.6 34.8 34.6', fill: 'none', stroke: p.ink, 'stroke-width': 1.6, 'stroke-linecap': 'round' }),
    thinking: h('path', { d: 'M30.2 35.6 Q 32.2 34.6 34.2 35.4', fill: 'none', stroke: p.ink, 'stroke-width': 1.6, 'stroke-linecap': 'round' }),
    writing: h('path', { d: 'M29.6 34.8 Q 32.2 37 34.6 34.6', fill: 'none', stroke: p.ink, 'stroke-width': 1.6, 'stroke-linecap': 'round' }),
    happy: h('g', {}, [
      h('path', { d: 'M28.4 33.8 Q 32 39.6 35.6 33.8 Z', fill: p.ink, stroke: p.ink, 'stroke-width': 1.2, 'stroke-linejoin': 'round' }),
      h('path', { d: 'M30.2 36.4 Q 32 37.8 33.8 36.4 Q 32 35.4 30.2 36.4 Z', fill: p.cheek }),
    ]),
    error: h('path', { d: 'M28.6 36.4 Q 30.3 34.8 32 36.2 Q 33.7 37.6 35.4 36', fill: 'none', stroke: p.ink, 'stroke-width': 1.6, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
  };
  nodes.push(h('g', { class: `${c}__mouth` }, [mouths[mood] || mouths.idle]));

  return nodes;
}

/**
 * Builds the quill pen Inky holds while writing, plus the line it draws.
 * @param {Object} p Palette.
 * @returns {Array} Nodes.
 */
function buildQuill(p) {
  const c = INKY_CLASS;
  return [
    h('path', {
      class: `${c}__ink-line`,
      d: 'M37 59.6 q 2.4 -1.8 4.8 0 t 4.8 0 t 4.8 0',
      fill: 'none',
      stroke: p.inkLine,
      'stroke-width': 1.3,
      'stroke-linecap': 'round',
      'stroke-dasharray': '16',
      'stroke-dashoffset': '0',
    }),
    h('g', { class: `${c}__quill` }, [
      // Feather vane.
      h('path', {
        d: 'M62.4 33.2 C 63.6 38.4, 60.6 45, 55.6 49.6 C 54.6 44.6, 57 37.6, 62.4 33.2 Z',
        fill: p.feather,
        stroke: p.featherStroke,
        'stroke-width': 0.8,
        'stroke-linejoin': 'round',
      }),
      // Barbs.
      h('path', {
        d: 'M59.4 39.6 L 61.4 39 M58.2 42.6 L 60.6 42.4 M57 45.4 L 59 45.8',
        fill: 'none',
        stroke: p.featherVane,
        'stroke-width': 0.7,
        'stroke-linecap': 'round',
      }),
      // Shaft.
      h('path', { d: 'M61.6 35.4 L 53.2 53.6', fill: 'none', stroke: p.featherStroke, 'stroke-width': 0.9, 'stroke-linecap': 'round' }),
      // Nib.
      h('path', { d: 'M54.6 50.4 L 52.4 55.8 L 55.8 51.2 Z', fill: p.nib, stroke: p.nib, 'stroke-width': 0.6, 'stroke-linejoin': 'round' }),
    ]),
  ];
}

/**
 * Builds the "thinking" bubble with three dots.
 * @param {Object} p Palette.
 * @returns {Object} Node.
 */
function buildThoughtBubble(p) {
  const c = INKY_CLASS;
  return h('g', { class: `${c}__bubble` }, [
    h('circle', { cx: 47.6, cy: 13.4, r: 1.2, fill: p.bubbleFill, stroke: p.bubbleStroke, 'stroke-width': 0.7 }),
    h('rect', { x: 47.2, y: 1.2, width: 16, height: 9.4, rx: 4.7, fill: p.bubbleFill, stroke: p.bubbleStroke, 'stroke-width': 0.8 }),
    h('circle', { class: `${c}__dot ${c}__dot--1`, cx: 51.4, cy: 5.9, r: 1.2, fill: p.bubbleDot }),
    h('circle', { class: `${c}__dot ${c}__dot--2`, cx: 55.2, cy: 5.9, r: 1.2, fill: p.bubbleDot }),
    h('circle', { class: `${c}__dot ${c}__dot--3`, cx: 59, cy: 5.9, r: 1.2, fill: p.bubbleDot }),
  ]);
}

/**
 * Builds the full SVG node tree for a mood.
 *
 * @param {Object} options
 * @param {string} [options.mood='idle'] Mood.
 * @param {'light'|'dark'} [options.scheme='light'] Colour scheme.
 * @param {string} [options.idPrefix='inky'] Prefix for gradient ids (must be unique per document).
 * @returns {{ tag: string, attrs: Object, children: Array }} Root `<g>` node (the caller wraps it in `<svg>`).
 */
export function buildInkyTree({ mood = 'idle', scheme = 'light', idPrefix = 'inky' } = {}) {
  const m = normalizeMood(mood);
  const p = INKY_PALETTES[scheme === 'dark' ? 'dark' : 'light'];
  const c = INKY_CLASS;
  const gradId = `${sanitizeId(idPrefix)}-head`;

  const tentacles = TENTACLES.map((t, index) => {
    const def = m === 'writing' && index === TENTACLES.length - 1 ? WRITING_ARM : t;
    return h('path', {
      class: `${c}__tentacle ${c}__tentacle--${index + 1}`,
      d: def.d,
      style: `transform-origin: ${def.root[0]}px ${def.root[1]}px`,
    });
  });

  // Suckers on the two outer tentacles' curls (skipped on the writing arm).
  const suckers = [
    h('circle', { cx: 9.4, cy: 49.2, r: 0.85 }),
    h('circle', { cx: 11.6, cy: 45.4, r: 0.75 }),
    h('circle', { cx: 19, cy: 54, r: 0.8 }),
  ];
  const mirroredSuckers = suckers
    .filter((s, i) => !(m === 'writing' && i < 2))
    .map((s) => h('circle', { ...s.attrs, cx: Math.round((64 - s.attrs.cx) * 100) / 100 }));

  const children = [
    h('defs', {}, [
      h('radialGradient', { id: gradId, cx: '38%', cy: '30%', r: '72%' }, [
        h('stop', { offset: '0%', 'stop-color': p.bodyLight }),
        h('stop', { offset: '100%', 'stop-color': p.body }),
      ]),
    ]),
    h('g', { class: `${c}__body` }, [
      h('g', { class: `${c}__tentacles`, fill: 'none', stroke: p.body, 'stroke-width': 4.6, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, tentacles),
      h('g', { class: `${c}__suckers`, fill: p.sucker, opacity: 0.85 }, [...suckers, ...mirroredSuckers]),
      // Skirt joins the head to the tentacle roots.
      h('ellipse', { cx: 32, cy: 40.2, rx: 16.6, ry: 4.6, fill: p.body }),
      h('ellipse', { class: `${c}__head`, cx: 32, cy: 25, rx: 18.6, ry: 17.6, fill: `url(#${gradId})` }),
      // Spots and a soft highlight on the dome.
      h('g', { class: `${c}__spots`, fill: p.spot }, [
        h('circle', { cx: 21.6, cy: 16.4, r: 2.1 }),
        h('circle', { cx: 42.6, cy: 13.6, r: 1.6 }),
        h('circle', { cx: 46, cy: 19.6, r: 1.1 }),
        h('circle', { cx: 18, cy: 21.6, r: 1 }),
      ]),
      h('ellipse', { cx: 26.6, cy: 12.6, rx: 5.4, ry: 2.6, fill: p.highlight, opacity: 0.28, transform: 'rotate(-18 26.6 12.6)' }),
      // Lighter belly band under the face.
      h('ellipse', { cx: 32, cy: 37.6, rx: 9.6, ry: 3.2, fill: p.belly, opacity: 0.35 }),
      h('g', { class: `${c}__face` }, buildFace(m, p)),
      ...(m === 'error'
        ? [h('path', { class: `${c}__sweat`, d: 'M47.6 15.4 C 46.2 17.6, 46 19, 47.6 19.6 C 49.2 19, 49 17.6, 47.6 15.4 Z', fill: p.sweat })]
        : []),
      ...(m === 'writing' ? buildQuill(p) : []),
    ]),
    ...(m === 'thinking' ? [buildThoughtBubble(p)] : []),
  ];

  return h('g', { class: `${c}__root` }, children);
}

/**
 * Keyframes and mood rules. Injected once per document by `InkyMascot`.
 * Every selector is scoped under `.q-inky`, and nothing animates unless the
 * root also carries `.q-inky--animated`.
 */
export const INKY_CSS = `
.${INKY_CLASS}{display:inline-block;flex:none;overflow:visible;vertical-align:middle}
.${INKY_CLASS} .${INKY_CLASS}__eyes{transform-origin:32px 28px}
.${INKY_CLASS} .${INKY_CLASS}__body{transform-origin:32px 58px}
.${INKY_CLASS} .${INKY_CLASS}__quill{transform-origin:53px 54px}
@keyframes ${INKY_CLASS}-blink{0%,90%,100%{transform:scaleY(1)}94%{transform:scaleY(.12)}}
@keyframes ${INKY_CLASS}-sway{0%,100%{transform:rotate(0deg)}50%{transform:rotate(4deg)}}
@keyframes ${INKY_CLASS}-sway-r{0%,100%{transform:rotate(0deg)}50%{transform:rotate(-4deg)}}
@keyframes ${INKY_CLASS}-wave{0%,100%{transform:rotate(-7deg)}50%{transform:rotate(7deg)}}
@keyframes ${INKY_CLASS}-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-1.8px)}}
@keyframes ${INKY_CLASS}-dot{0%,70%,100%{opacity:.25}35%{opacity:1}}
@keyframes ${INKY_CLASS}-scribble{0%,100%{transform:rotate(0deg) translate(0,0)}25%{transform:rotate(-6deg) translate(-.6px,.4px)}50%{transform:rotate(2deg) translate(.4px,0)}75%{transform:rotate(-4deg) translate(-.3px,.5px)}}
@keyframes ${INKY_CLASS}-wiggle{0%,100%{transform:rotate(0deg)}30%{transform:rotate(-2.5deg)}70%{transform:rotate(2deg)}}
@keyframes ${INKY_CLASS}-ink{0%{stroke-dashoffset:16}60%,100%{stroke-dashoffset:0}}
@keyframes ${INKY_CLASS}-bounce{0%,100%{transform:translateY(0) scale(1,1)}30%{transform:translateY(-4px) scale(.98,1.03)}55%{transform:translateY(0) scale(1.04,.96)}70%{transform:translateY(-1px) scale(1,1)}}
@keyframes ${INKY_CLASS}-shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-1.2px)}40%{transform:translateX(1.2px)}60%{transform:translateX(-.8px)}80%{transform:translateX(.6px)}}
.${INKY_CLASS}--animated .${INKY_CLASS}__eyes:not(.${INKY_CLASS}__eyes--closed){animation:${INKY_CLASS}-blink 4.8s ease-in-out infinite}
.${INKY_CLASS}--idle.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle:nth-child(-n+3){animation:${INKY_CLASS}-sway 4.2s ease-in-out infinite}
.${INKY_CLASS}--idle.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle:nth-child(n+4){animation:${INKY_CLASS}-sway-r 4.2s ease-in-out infinite}
.${INKY_CLASS}--thinking.${INKY_CLASS}--animated .${INKY_CLASS}__body{animation:${INKY_CLASS}-bob 2.4s ease-in-out infinite}
.${INKY_CLASS}--thinking.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle{animation:${INKY_CLASS}-wave 1.8s ease-in-out infinite}
.${INKY_CLASS}--thinking.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle:nth-child(2n){animation-delay:-.9s}
.${INKY_CLASS}--thinking.${INKY_CLASS}--animated .${INKY_CLASS}__dot{animation:${INKY_CLASS}-dot 1.2s ease-in-out infinite}
.${INKY_CLASS}--thinking.${INKY_CLASS}--animated .${INKY_CLASS}__dot--2{animation-delay:.2s}
.${INKY_CLASS}--thinking.${INKY_CLASS}--animated .${INKY_CLASS}__dot--3{animation-delay:.4s}
.${INKY_CLASS}--writing.${INKY_CLASS}--animated .${INKY_CLASS}__body{animation:${INKY_CLASS}-wiggle 1.6s ease-in-out infinite}
.${INKY_CLASS}--writing.${INKY_CLASS}--animated .${INKY_CLASS}__quill{animation:${INKY_CLASS}-scribble .8s ease-in-out infinite}
.${INKY_CLASS}--writing.${INKY_CLASS}--animated .${INKY_CLASS}__ink-line{animation:${INKY_CLASS}-ink 1.6s ease-in-out infinite}
.${INKY_CLASS}--writing.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle:nth-child(-n+3){animation:${INKY_CLASS}-sway 1.6s ease-in-out infinite}
.${INKY_CLASS}--happy.${INKY_CLASS}--animated .${INKY_CLASS}__body{animation:${INKY_CLASS}-bounce 1.4s cubic-bezier(.3,.7,.4,1) infinite}
.${INKY_CLASS}--happy.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle{animation:${INKY_CLASS}-wave 1.4s ease-in-out infinite}
.${INKY_CLASS}--happy.${INKY_CLASS}--animated .${INKY_CLASS}__tentacle:nth-child(2n){animation-delay:-.7s}
.${INKY_CLASS}--error.${INKY_CLASS}--animated .${INKY_CLASS}__body{animation:${INKY_CLASS}-shake .5s ease-in-out 1}
.${INKY_CLASS}--error.${INKY_CLASS}--animated .${INKY_CLASS}__eyes:not(.${INKY_CLASS}__eyes--closed){animation:none}
.${INKY_CLASS}-working{display:inline-flex;align-items:center;gap:8px;min-width:0}
.${INKY_CLASS}-working__label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.${INKY_CLASS}-ellipsis{display:inline-flex;gap:1px;margin-left:1px}
.${INKY_CLASS}-ellipsis>span{animation:${INKY_CLASS}-dot 1.2s ease-in-out infinite}
.${INKY_CLASS}-ellipsis>span:nth-child(2){animation-delay:.2s}
.${INKY_CLASS}-ellipsis>span:nth-child(3){animation-delay:.4s}
@media (prefers-reduced-motion: reduce){
.${INKY_CLASS},.${INKY_CLASS} *,.${INKY_CLASS}-ellipsis>span{animation:none !important;transition:none !important}
.${INKY_CLASS}-ellipsis>span{opacity:1}
}
`.trim();

/**
 * Class list for the root `<svg>`.
 * @param {string} mood Normalised mood.
 * @param {boolean} animated Whether motion is enabled.
 * @param {string} [extra] Additional classes.
 * @returns {string} Class string.
 */
export function inkyRootClass(mood, animated, extra) {
  return [INKY_CLASS, `${INKY_CLASS}--${normalizeMood(mood)}`, animated ? `${INKY_CLASS}--animated` : '', extra || '']
    .filter(Boolean)
    .join(' ');
}

const escapeAttr = (value) =>
  String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/**
 * Serialises a node tree to SVG markup.
 * @param {{ tag: string, attrs: Object, children: Array }} node Node.
 * @returns {string} Markup.
 */
export function nodeToMarkup(node) {
  const attrs = Object.entries(node.attrs)
    .filter(([, v]) => v !== undefined && v !== null && v !== false)
    .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
    .join('');
  const children = node.children.map(nodeToMarkup).join('');
  return `<${node.tag}${attrs}>${children}</${node.tag}>`;
}

/**
 * Renders a complete standalone `<svg>` string for Inky.
 *
 * @param {Object} [options]
 * @param {number} [options.size=32] Rendered size in px.
 * @param {string} [options.mood='idle'] Mood.
 * @param {'light'|'dark'} [options.scheme='light'] Colour scheme.
 * @param {boolean} [options.animated=true] Adds the animation class.
 * @param {string} [options.title='Inky'] Accessible name; pass `null` for a decorative image.
 * @param {string} [options.idPrefix] Unique prefix for internal ids.
 * @returns {string} SVG markup.
 */
export function renderInkyMarkup({ size = 32, mood = 'idle', scheme = 'light', animated = true, title = 'Inky', idPrefix } = {}) {
  const m = normalizeMood(mood);
  const prefix = sanitizeId(idPrefix || `inky-${m}-${scheme}-${size}`);
  const a11y = title
    ? `role="img" aria-label="${escapeAttr(title)}"`
    : 'aria-hidden="true" focusable="false"';
  const body = nodeToMarkup(buildInkyTree({ mood: m, scheme, idPrefix: prefix }));
  return `<svg xmlns="http://www.w3.org/2000/svg" class="${inkyRootClass(m, animated)}" width="${size}" height="${size}" viewBox="0 0 64 64" ${a11y}>${body}</svg>`;
}
